import React, { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { safeJson } from "@/lib/api";
import { Spinner } from "@/components/Spinner";
import Layout from "@/components/Layout";
import ProfileSetup from "@/screens/onboarding/ProfileSetup";
import CloudbetSetup from "@/screens/onboarding/CloudbetSetup";
import TelegramSetup from "@/screens/onboarding/TelegramSetup";
import Matches from "@/screens/Matches";
import Trading from "@/screens/Trading";
import SetHandicap from "@/screens/SetHandicap";
import MatchWinner from "@/screens/MatchWinner";
import Settings from "@/screens/Settings";
import AdminPanel from "@/screens/AdminPanel";

export default function MainContent({ isAdmin, onLogout }) {
  const { profileComplete, setProfileComplete, profileLoaded, apiFetch } = useAuth();
  const [activeView, setActiveView] = useState("matches");
  const [onboardingStep, setOnboardingStep] = useState(isAdmin ? "done" : "checking");
  const [setHandicapEnabled, setSetHandicapEnabled] = useState(false);
  const [matchWinnerEnabled, setMatchWinnerEnabled] = useState(false);

  // Set Handicap & Match Winner tabs are only shown when the user has that strategy enabled.
  // Re-checked on every tab switch AND whenever Settings toggles a strategy (via refreshStrategyTabs).
  const refreshStrategyTabs = React.useCallback(async () => {
    try {
      const res = await apiFetch("/api/user/profile");
      const data = await safeJson(res);
      const shOn = !!(data?.strategies?.["Set Handicap Strategy"]?.enabled);
      const mwOn = !!(data?.strategies?.["Match Winner Strategy"]?.enabled);
      setSetHandicapEnabled(shOn);
      setMatchWinnerEnabled(mwOn);
      setActiveView((v) => {
        if (!shOn && v === "set-handicap") return "matches";
        if (!mwOn && v === "match-winner") return "matches";
        return v;
      });
    } catch { /* keep previous state */ }
  }, [apiFetch]);

  useEffect(() => { refreshStrategyTabs(); }, [activeView, refreshStrategyTabs]);

  useEffect(() => {
    if (!profileComplete || isAdmin || onboardingStep !== "checking") return;
    (async () => {
      try {
        const res = await apiFetch("/api/user/profile");
        const data = await safeJson(res);
        const hasCb = data?.has_cloudbet_key || false;
        const hasTg = data?.has_telegram_id || false;
        if (hasCb && hasTg) setOnboardingStep("done");
        else if (hasCb) setOnboardingStep("telegram");
        else setOnboardingStep("cloudbet");
      } catch {
        setOnboardingStep("cloudbet");
      }
    })();
  }, [profileComplete, isAdmin, onboardingStep, apiFetch]);

  const center = (
    <div className="min-h-screen flex items-center justify-center bg-[#0f172a]">
      <Spinner />
    </div>
  );

  // Show the entry spinner only until we actually know the profile state (fast ~0.15s check),
  // never a fixed delay.
  if (!isAdmin && !profileLoaded && !profileComplete) return center;

  if (!profileComplete && !isAdmin) {
    return <ProfileSetup apiFetch={apiFetch} onComplete={() => setProfileComplete(true)} onBack={onLogout} />;
  }
  if (onboardingStep === "cloudbet" && !isAdmin) {
    return <CloudbetSetup apiFetch={apiFetch} onDone={() => setOnboardingStep("telegram")} />;
  }
  if (onboardingStep === "telegram" && !isAdmin) {
    return <TelegramSetup apiFetch={apiFetch} onDone={() => setOnboardingStep("done")} />;
  }
  if (onboardingStep === "checking" && !isAdmin) return center;

  // Screens unmount on tab switch (stops their timers/WebSocket), but each screen seeds from a
  // module-level cache so returning renders instantly with a silent background refresh — no
  // spinner flash on repeat visits.
  return (
    <Layout activeView={activeView} setActiveView={setActiveView} isAdmin={isAdmin} onLogout={onLogout} showSetHandicap={setHandicapEnabled} showMatchWinner={matchWinnerEnabled}>
      {activeView === "matches" && <Matches />}
      {activeView === "trading" && <Trading onNavigate={setActiveView} />}
      {activeView === "set-handicap" && <SetHandicap />}
      {activeView === "match-winner" && <MatchWinner />}
      {activeView === "settings" && (
        <Settings onOpenAdmin={isAdmin ? () => setActiveView("admin") : undefined} onLogout={onLogout} onStrategyToggle={refreshStrategyTabs} />
      )}
      {activeView === "admin" && isAdmin && <AdminPanel onClose={() => setActiveView("settings")} />}
    </Layout>
  );
}
